import React, { useMemo, useState, useCallback } from 'react';
import { Button, Modal, Form, Input, Select, message } from 'antd';
import s from './s.module.less';
import type { ProviderBillPayoutDetailVo } from 'types/providerBill';
import { saveProviderBillPayoutDraft } from 'api/appointmentSetting';
import StateSelect from 'components/form/StateSelect';

const TAX_ID_TYPE_OPTIONS = [
    { value: 'EIN', label: 'EIN' },
    { value: 'SSN', label: 'SSN' },
];

type Props = Readonly<{
    open: boolean;
    onClose: () => void;
    initialValue?: ProviderBillPayoutDetailVo;
    onSaved: () => void;
}>;

export default function BillPayoutSetupModal({ open, onClose, initialValue, onSaved }: Props) {
    const [basicForm] = Form.useForm();
    const [accountForm] = Form.useForm();
    const [saving, setSaving] = useState(false);

    const initBasic = useMemo(
        () => ({
            firstName: initialValue?.firstName || '',
            lastName: initialValue?.lastName || '',
            addressLine1: initialValue?.addressLine1 || '',
            city: initialValue?.city || '',
            state: initialValue?.state || '',
            zip: initialValue?.zip || '',
            email: initialValue?.email || '',
        }),
        [initialValue],
    );

    const initAccount = useMemo(
        () => ({
            companyName: initialValue?.companyName || '',
            taxIdType: initialValue?.taxIdType || 'EIN',
            taxId: initialValue?.taxId || '',
            accountNumber: initialValue?.accountNumber || '',
            routingNumber: initialValue?.routingNumber || '',
        }),
        [initialValue],
    );

    const handleAfterOpenChange = useCallback((nextOpen: boolean) => {
        if (!nextOpen) {
            setSaving(false);
        }
    }, []);

    const handleSave = async () => {
        try {
            const basic = await basicForm.validateFields();
            const account = await accountForm.validateFields();
            setSaving(true);
            await saveProviderBillPayoutDraft({ ...basic, ...account });
            message.success('Payout account saved');
            onSaved();
            onClose();
        } catch (err) {
            console.error('BillPayoutSetupModal save failed:', err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            width={912}
            centered
            destroyOnClose
            onCancel={onClose}
            title="Set up payout account"
            className={s.modal}
            afterOpenChange={handleAfterOpenChange}
            footer={
                <div className={s.footer}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" onClick={handleSave} loading={saving}>
                        Save
                    </Button>
                </div>
            }
        >
            <div className={s.body}>
                <div className={s.section}>
                    <div className={s.sectionHeader}>
                        <div className={s.sectionTitle}>Basic information</div>
                    </div>
                    <Form form={basicForm} layout="vertical" requiredMark={false} initialValues={initBasic}>
                        <div className={s.formRow}>
                            <Form.Item label="First name" name="firstName" className={s.formCol}>
                                <Input />
                            </Form.Item>
                            <Form.Item label="Last name" name="lastName" className={s.formCol}>
                                <Input />
                            </Form.Item>
                        </div>
                        <div className={s.formRow}>
                            <div className={s.addressCol}>
                                <Form.Item
                                    label={
                                        <span>
                                            Address <span className={s.required}>*</span>
                                        </span>
                                    }
                                    name="addressLine1"
                                    rules={[{ required: true, message: 'Please enter address' }]}
                                >
                                    <Input />
                                </Form.Item>
                                <div className={s.addressSubRow}>
                                    <Form.Item
                                        label={
                                            <span>
                                                City <span className={s.required}>*</span>
                                            </span>
                                        }
                                        name="city"
                                        className={s.cityField}
                                        rules={[{ required: true, message: 'Please enter city' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                    <Form.Item
                                        label={
                                            <span>
                                                State <span className={s.required}>*</span>
                                            </span>
                                        }
                                        name="state"
                                        className={s.stateField}
                                        rules={[{ required: true, message: 'Please select state' }]}
                                    >
                                        <StateSelect />
                                    </Form.Item>
                                    <Form.Item
                                        label={
                                            <span>
                                                ZIP code <span className={s.required}>*</span>
                                            </span>
                                        }
                                        name="zip"
                                        className={s.zipField}
                                        rules={[{ required: true, message: 'Please enter ZIP' }]}
                                    >
                                        <Input />
                                    </Form.Item>
                                </div>
                            </div>
                            <Form.Item label="Email address" name="email" className={s.formCol}>
                                <Input placeholder="Enter your email address" />
                            </Form.Item>
                        </div>
                    </Form>
                </div>

                <div className={s.section}>
                    <div className={s.sectionHeader}>
                        <div className={s.sectionTitle}>Account info</div>
                    </div>
                    <Form form={accountForm} layout="vertical" initialValues={initAccount}>
                        <Form.Item label="Company name" name="companyName" className={s.halfField}>
                            <Input />
                        </Form.Item>
                        <div className={s.formRow}>
                            <Form.Item label="Tax ID type" name="taxIdType" className={s.formCol}>
                                <Select options={TAX_ID_TYPE_OPTIONS} />
                            </Form.Item>
                            <Form.Item label="Tax ID" name="taxId" className={s.formCol}>
                                <Input />
                            </Form.Item>
                        </div>
                        <div className={s.formRow}>
                            <Form.Item label="Account number" name="accountNumber" className={s.formCol}>
                                <Input />
                            </Form.Item>
                            <Form.Item label="Routing number" name="routingNumber" className={s.formCol}>
                                <Input />
                            </Form.Item>
                        </div>
                    </Form>
                </div>
            </div>
        </Modal>
    );
}
